import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { UnleashClient } from "unleash-proxy-client"

import { useConfig } from "../utils/hook"

const UNLEASH_ERROR_PREFIX = "[Unleash]"

const UnleashContext = createContext(null)

export function UnleashProvider({ children }) {
  const config = useConfig()
  const { userId, whitelabelId } = useSelector(state => ({
    userId: state.user?.profile?.id || "guest",
    whitelabelId: state.user?.profile?.whitelabelId,
  }))

  const clientRef = useRef(null)
  const [flags, setFlags] = useState({})

  useEffect(() => {
    const hasUrl =
      config.REACT_APP_UNLEASH_URL &&
      config.REACT_APP_UNLEASH_URL !== "undefined" &&
      config.REACT_APP_UNLEASH_URL !== null
    const hasClientKey =
      config.REACT_APP_UNLEASH_CLIENT_KEY &&
      config.REACT_APP_UNLEASH_CLIENT_KEY !== "undefined" &&
      config.REACT_APP_UNLEASH_CLIENT_KEY !== null

    if (!hasUrl || !hasClientKey) {
      // eslint-disable-next-line no-undef, no-console
      console.error(`${UNLEASH_ERROR_PREFIX} Missing required configuration`)
      return
    }

    if (clientRef.current) return // Prevent duplicate init

    const unleashClient = new UnleashClient({
      url: config.REACT_APP_UNLEASH_URL,
      clientKey: config.REACT_APP_UNLEASH_CLIENT_KEY,
      environment: "production",
      appName: "default",
    })

    clientRef.current = unleashClient

    const updateFlags = () => {
      const toggles = unleashClient.getAllToggles()
      const flagMap = toggles.reduce((acc, toggle) => {
        acc[toggle.name] = toggle.enabled
        return acc
      }, {})
      setFlags(flagMap)
    }

    unleashClient.on("ready", updateFlags)
    unleashClient.on("update", updateFlags)
    unleashClient.on("error", err => {
      // eslint-disable-next-line no-console
      console.error("[Unleash] Error:", err)
    })

    unleashClient.start()

    return () => {
      unleashClient.stop()
    }
  }, [config])

  // Update context only when userId changes
  useEffect(() => {
    if (clientRef.current && userId) {
      clientRef.current.updateContext({ userId, whitelabelId })
    }
  }, [userId, whitelabelId])

  const flagsValue = useMemo(() => ({ client: clientRef.current, flags }), [flags])

  return <UnleashContext.Provider value={flagsValue}>{children}</UnleashContext.Provider>
}

export function useFlag(flagName) {
  const context = useContext(UnleashContext)
  if (!context) {
    // eslint-disable-next-line no-console
    console.warn(`[Unleash] useFlag("${flagName}") used outside provider`)
    return false
  }

  const { flags } = context
  return flags?.[flagName] ?? false
}
